import React, { useEffect, useState } from "react";
import { Flex, Spin, Tag, TimePicker, Typography, Alert } from "antd";
import { ClockCircleOutlined, InfoCircleFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { updateOrganization } from "../../redux/organizationSlice";
import SaveButton from "../UI/savebutton";
import { toDayjs, fromDayjs } from "../../utils/timeRange";

const { Title, Text } = Typography;

const WEEK_DAYS = [
  { key: "sun", label: "Sun" },
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
];

const DEFAULT_HOURS = { start: 8, end: 17, days: ["mon", "tue", "wed", "thu", "fri"] };

function OfficeHours() {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [isDirty, setIsDirty] = useState(false);

  const { loading, organizationData } = useSelector(
    (state) => state?.organization
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setHours(organizationData?.office_hours ?? DEFAULT_HOURS);
  }, [organizationData]);

  const update = (patch) => {
    setHours((prev) => ({ ...prev, ...patch }));
    setIsDirty(true);
  };

  const toggleDay = (dayKey) => {
    const days = hours.days?.includes(dayKey)
      ? hours.days.filter((d) => d !== dayKey)
      : [...(hours.days || []), dayKey];
    update({ days });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch(
      updateOrganization({
        id: organizationData._id,
        payload: { ...organizationData, office_hours: hours },
        page: "Office Hours",
      })
    );
    setIsDirty(false);
  };

  return (
    <form style={{ width: "100%" }}>
      <Spin spinning={loading} fullscreen />
      <Flex align="center" gap={10} style={{ marginBottom: "20px" }}>
        <ClockCircleOutlined style={{ fontSize: 20 }} />
        <Title level={4} style={{ color: "#333" }}>
          Office Hours
        </Title>
      </Flex>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6 flex flex-col gap-4 items-start w-full">
        <Alert
          showIcon
          icon={<InfoCircleFilled />}
          type="info"
          message="Setting hours here does not restrict any jobs by itself. Go to Job Mapping and mark a specific job as 'Office Hours' to limit it to the hours below — all jobs default to 24/7 and are unaffected unless changed there."
        />

        <div className="flex flex-col items-start gap-2">
          <label style={{ fontWeight: "bold" }}>Hours</label>
          <Flex align="center" gap={8}>
            <TimePicker
              use12Hours
              format="h:mm a"
              minuteStep={15}
              allowClear={false}
              value={toDayjs(hours.start ?? DEFAULT_HOURS.start)}
              onChange={(v) => update({ start: fromDayjs(v) })}
              style={{ width: 130 }}
            />
            <Text type="secondary">to</Text>
            <TimePicker
              use12Hours
              format="h:mm a"
              minuteStep={15}
              allowClear={false}
              value={toDayjs(hours.end ?? DEFAULT_HOURS.end)}
              onChange={(v) => update({ end: fromDayjs(v) })}
              style={{ width: 130 }}
            />
          </Flex>
        </div>

        <div className="flex flex-col items-start gap-2">
          <label style={{ fontWeight: "bold" }}>Apply to Days</label>
          <Flex gap={8} wrap>
            {WEEK_DAYS.map((day) => {
              const checked = hours.days?.includes(day.key);
              return (
                <Tag.CheckableTag
                  key={day.key}
                  checked={checked}
                  onChange={() => toggleDay(day.key)}
                  style={{
                    padding: "4px 14px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    userSelect: "none",
                    border: `1px solid ${checked ? "#2c6a99" : "#e2e8f0"}`,
                    background: checked ? "#337ab7" : "#f8fafc",
                    color: checked ? "#ffffff" : "#94a3b8",
                  }}
                >
                  {day.label}
                </Tag.CheckableTag>
              );
            })}
          </Flex>
        </div>

        <Text type="secondary" style={{ fontSize: "12px", fontStyle: "italic" }}>
          {hours.days?.length
            ? `Open ${WEEK_DAYS.filter((d) => hours.days.includes(d.key)).map((d) => d.label).join(", ")}. Closed the rest of the week.`
            : "No days selected — closed all week."}
        </Text>
      </div>

      <SaveButton onSubmit={onSubmit} isDirty={isDirty} />
    </form>
  );
}

export default OfficeHours;
